import { useSyncExternalStore } from 'react';
import { KOUTO_EMBED_ROOT } from 'consts';
import { getChangeSetIdFromUrl, isChangeSetId } from './getChangeSetIdFromUrl';

export interface ChangeSetPreview {
  /** The set being previewed, while the service still answers for it. */
  readonly changeSetId?: string;
  /** True once catalogs answered that the set is gone, closed or stale. */
  readonly isUnavailable: boolean;
}

/** No preview: the storefront is the published one, and no header is sent. */
const LIVE: ChangeSetPreview = { isUnavailable: false };

/**
 * `data-change-set` on the embed root first, then `?changeSet=` in the URL — the same order
 * `data-brand-id` and the `/brands/<id>` pathname resolve in. A host page that pins a draft does
 * so with the attribute; the dashboard's Preview button hands out the URL.
 */
const resolve = (): ChangeSetPreview => {
  const attribute =
    document.getElementById(KOUTO_EMBED_ROOT)?.dataset.changeSet;
  const changeSetId = isChangeSetId(attribute)
    ? attribute
    : getChangeSetIdFromUrl();

  return changeSetId ? { changeSetId, isUnavailable: false } : LIVE;
};

/**
 * Resolved once, on first use, and then fixed for the life of the page. It has to be readable
 * outside React — the catalogs request interceptor is module scope — and it has to stay still,
 * because every catalogs SWR key is scoped by it. Lazy rather than eager so the embed root is in
 * the document by the time its attribute is read, whichever way the host loaded the script.
 */
const store: { snapshot?: ChangeSetPreview } = {};
const listeners = new Set<() => void>();

const read = (): ChangeSetPreview => {
  if (!store.snapshot) store.snapshot = resolve();
  return store.snapshot;
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** The set a catalogs request should carry, or nothing when there is no live preview. */
export const getPreviewChangeSetId = (): string | undefined =>
  read().changeSetId;

/**
 * The service said the set is gone, closed or stale, so the preview is over and cannot come back
 * — there is no rebase. The header stops being sent, every catalogs key loses the id and so
 * refetches the published storefront, and the banner says the draft is no longer available
 * instead of hiding that the page changed under the reader.
 */
export const markChangeSetUnavailable = (): void => {
  if (!read().changeSetId) return;
  store.snapshot = { isUnavailable: true };
  listeners.forEach((listener) => listener());
};

/** The preview as React sees it: re-renders when a preview ends. */
export const useChangeSetPreview = (): ChangeSetPreview =>
  useSyncExternalStore(subscribe, read, read);
